// #region Imports

// --- Angular
import { Injectable, NgZone } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, of } from 'rxjs';
import { take } from 'rxjs/operators';

// --- 3rd Party
import { User } from 'oidc-client';

// -- HS Code
import { environment } from '../../../../environments/environment';
import { EnvService } from '../env.service';
import { LoggerService } from '../logging/logger.service';
import { ExistingIframeService } from './existing-iframe.service';
import { Constants, EventTypes } from '../../../models';
import { EventService } from '../event.service';
import { IdleTimeoutService } from '../idle-timeout.service';

// #endregion

const IFRAME_FOR_CHECK_SESSION_IDENTIFIER = 'myiFrameForCheckSession';

@Injectable({
  providedIn: 'root'
})
export class CheckSessionService {
  private checkSessionReceived = false;
  private scheduledHeartBeatRunning: any;
  private lastIFrameRefresh = 0;
  private outstandingMessages = 0;
  private heartBeatInterval = 3000;
  private iframeRefreshInterval = 60000;
  private _clientId: string;
  private _shouldCheckSession: boolean;
  private checkSessionChangedInternal$ = new BehaviorSubject<boolean>(false);
  get checkSessionChanged$() {
    return this.checkSessionChangedInternal$.asObservable();
  }

  private _identity: User | null = null;

  constructor(
    private loggerService: LoggerService,
    private iFrameService: ExistingIframeService,
    private eventService: EventService,
    private envService: EnvService,
    private zone: NgZone,
    private idleTimeoutService: IdleTimeoutService
  ) {
    this._clientId = envService.clientId;
    this._shouldCheckSession = envService.getBoolean('shouldCheckSession');
    this.idleTimeoutService.idleState$.subscribe((idleState) => {
      if (idleState === 'active' && !this.scheduledHeartBeatRunning && this._identity) {
        this.start(this._identity);
      } else if (idleState === 'idle' && !!this.scheduledHeartBeatRunning) {
        this.stop();
      }
    });
  }

  isCheckSessionConfigured() {
    return this._shouldCheckSession;
  }

  start(identity: User): void {
    // tslint:disable-next-line:no-debugger
    if (!!this.scheduledHeartBeatRunning) {
      return;
    }

    this._identity = identity;
    const clientId = this._clientId;
    this.pollServerSession(clientId);
  }

  stop(): void {
    if (!this.scheduledHeartBeatRunning) {
      return;
    }

    this.clearScheduledHeartBeat();
    this.checkSessionReceived = false;
  }

  serverStateChanged() {
    return this._shouldCheckSession && this.checkSessionReceived;
  }

  getExistingIframe() {
    return this.iFrameService.getExistingIFrame(IFRAME_FOR_CHECK_SESSION_IDENTIFIER);
  }

  private init(): Observable<any> {
    if (this.lastIFrameRefresh + this.iframeRefreshInterval > Date.now()) {
      return of(undefined);
    }

    const existingIframe = this.getOrCreateIframe();
    const checkSessionIframe = environment.authSettings.authority + Constants.CHECK_SESSION_ENDPOINT;

    if (checkSessionIframe && existingIframe && existingIframe.contentWindow && existingIframe.contentWindow.location) {
      existingIframe.contentWindow.location.replace(checkSessionIframe);
    } else {
      this.loggerService.logWarning('init check session: checkSessionIframe is not configured to run');
    }

    return new Observable((observer) => {
      existingIframe.onload = () => {
        this.lastIFrameRefresh = Date.now();
        observer.next();
        observer.complete();
      };
    });
  }

  private pollServerSession(clientId: string) {
    this.outstandingMessages = 0;
    const pollServerSessionRecur = () => {
      this.init()
        .pipe(take(1))
        .subscribe(() => {
          if (this._identity) {
            const identity = this._identity;
            const existingIframe = this.getExistingIframe();
            if (existingIframe && clientId) {
              this.loggerService.logDebug(existingIframe);
              const sessionState = identity.session_state;

              if (sessionState && existingIframe && existingIframe.contentWindow) {
                const iframeOrigin = new URL(environment.authSettings.authority + Constants.CHECK_SESSION_ENDPOINT).origin;
                this.outstandingMessages++;
                existingIframe.contentWindow.postMessage(clientId + ' ' + sessionState,
                  environment.authSettings.authority + Constants.CHECK_SESSION_ENDPOINT);
              } else {
                this.loggerService.logDebug(`OidcSecurityCheckSession pollServerSession session_state is '${sessionState}'`);
                this.loggerService.logDebug(
                  `AuthWellKnownEndPoints is '${JSON.stringify(environment.authSettings.authority + Constants.CHECK_SESSION_ENDPOINT)}'`);
                this.checkSessionChangedInternal$.next(true);
              }
            } else {
              this.loggerService.logWarning('OidcSecurityCheckSession pollServerSession checkSession IFrame does not exist');
              this.loggerService.logDebug(clientId);
              this.loggerService.logDebug(existingIframe);
            }

            // after sending three messages with no response, fail.
            if (this.outstandingMessages > 3) {
              this.loggerService.logError(
                `OidcSecurityCheckSession not receiving check session response messages.
                              Outstanding messages: ${this.outstandingMessages}. Server unreachable?`
              );
            }

            this.zone.runOutsideAngular(() => {
              this.scheduledHeartBeatRunning = setTimeout(() => this.zone.run(pollServerSessionRecur), this.heartBeatInterval);
            });
          }
        });
    };

    pollServerSessionRecur();
  }

  private clearScheduledHeartBeat() {
    clearTimeout(this.scheduledHeartBeatRunning);
    this.scheduledHeartBeatRunning = null;
  }

  private messageHandler(e: any) {
    const existingIFrame = this.getExistingIframe();
    const startsWith = !!(environment.authSettings.authority + Constants.CHECK_SESSION_ENDPOINT).startsWith(e.origin);
    this.outstandingMessages = 0;
    if (existingIFrame && startsWith && e.source === existingIFrame.contentWindow) {
      if (e.data === 'error') {
        this.loggerService.logWarning('error from checksession messageHandler');
      } else if (e.data === 'changed') {
        this.loggerService.logDebug(e);
        this.checkSessionReceived = true;
        this.eventService.fireEvent(EventTypes.CheckSessionReceived, e.data);
        this.checkSessionChangedInternal$.next(true);
      } else {
        this.eventService.fireEvent(EventTypes.CheckSessionReceived, e.data);
        this.loggerService.logDebug(e.data + ' from checksession messageHandler');
      }
    }
  }

  private bindMessageEventToIframe() {
    const iframeMessageEvent = this.messageHandler.bind(this);
    window.addEventListener('message', iframeMessageEvent, false);
  }

  private getOrCreateIframe() {
    const existingIframe = this.getExistingIframe();

    if (!existingIframe) {
      const frame = this.iFrameService.addIFrameToWindowBody(IFRAME_FOR_CHECK_SESSION_IDENTIFIER);
      this.bindMessageEventToIframe();
      return frame;
    }

    return existingIframe;
  }
}
